import React, { Component } from "react";
import DeliveryService from "../../services/DeliveryService";
import DeliveryEntity from "../../types/deliveryEntity.type";
import DeliveryList from "./DeliveryList";
import EmptyPostedDeliveriesView from "./EmptyPostedDeliveriesView";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";

type Props = {
  userId?: number | null;
  fullName?: string | null;
};

type State = {
  deliveryEntity: DeliveryEntity[];
  editingDelivery: DeliveryEntity | null;
  errorMessage?: string;
};

export default class DeliveriesView extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      deliveryEntity: [],
      editingDelivery: null,
      errorMessage: undefined,
    };
    this.handleEdit = this.handleEdit.bind(this);
    this.handleDelete = this.handleDelete.bind(this);
    this.handleStatusChange = this.handleStatusChange.bind(this);
    this.handleSaveDelivery = this.handleSaveDelivery.bind(this);
  }

  componentDidMount() {
    this.fetchDeliveries();
  }

  fetchDeliveries() {
    const apiCall = this.props.userId != null && this.props.userId > 0
      ? DeliveryService.getUserDeliveries(this.props.userId)
      : DeliveryService.getDeliveriesBoard();

    apiCall
      .then((response) => {
        console.log("API response:", response.data);
        const deliveries = Array.isArray(response.data) ? response.data : [];
        this.setState({ deliveryEntity: deliveries, errorMessage: undefined });
      })
      .catch((error) => {
        const errorMsg =
          error.response?.data?.message || error.message || "Failed to fetch deliveries";
        console.error("Fetch error:", errorMsg);
        this.setState({
          deliveryEntity: [],
          errorMessage: errorMsg,
        });
      });
  }

  handleEdit = (delivery: DeliveryEntity) => {
    console.log("Editing delivery:", delivery);
    this.setState({ editingDelivery: delivery });
  };

  handleDelete = (deliveryId: number) => {
    if (window.confirm("Are you sure you want to delete this delivery?")) {
      DeliveryService.deleteDelivery(deliveryId)
        .then(() => {
          this.setState((prevState) => ({
            deliveryEntity: prevState.deliveryEntity.filter((d) => d.id !== deliveryId),
            errorMessage: undefined,
          }));
        })
        .catch((error) => {
          const errorMsg =
            error.response?.data?.message || error.message || "Failed to delete delivery";
          console.error("Delete error:", errorMsg);
          this.setState({ errorMessage: errorMsg });
        });
    }
  };

  handleStatusChange = (deliveryId: number, status: string) => {
    console.log(`Updating status for delivery ID ${deliveryId}: ${status}`);
    const updatedDelivery = this.state.deliveryEntity.find((d) => d.id === deliveryId);
    if (updatedDelivery) {
      const { createdAt, updatedAt, ...deliveryData } = updatedDelivery; // Exclude createdAt, updatedAt
      const newDelivery = { ...deliveryData, status };
      DeliveryService.updateDelivery(newDelivery)
        .then(() => {
          this.setState((prevState) => ({
            deliveryEntity: prevState.deliveryEntity.map((d) =>
              d.id === deliveryId ? { ...newDelivery, createdAt: d.createdAt, updatedAt: d.updatedAt } : d
            ),
            errorMessage: undefined,
          }));
        })
        .catch((error) => {
          const errorMsg =
            error.response?.data?.message || error.message || "Failed to update status";
          console.error("Status update error:", errorMsg);
          this.setState({ errorMessage: errorMsg });
        });
    }
  };

  handleSaveDelivery = (updatedDelivery: DeliveryEntity) => {
    console.log("Saving delivery:", updatedDelivery);
    const { createdAt, updatedAt, ...deliveryData } = updatedDelivery; // Exclude createdAt, updatedAt
    DeliveryService.updateDelivery(deliveryData)
      .then(() => {
        this.setState((prevState) => ({
          deliveryEntity: prevState.deliveryEntity.map((d) =>
            d.id === updatedDelivery.id ? { ...deliveryData, createdAt: d.createdAt, updatedAt: d.updatedAt } : d
          ),
          editingDelivery: null,
          errorMessage: undefined,
        }));
      })
      .catch((error) => {
        const errorMsg =
          error.response?.data?.message || error.message || "Failed to update delivery";
        console.error("Update error:", errorMsg);
        this.setState({ errorMessage: errorMsg });
      });
  };

  render() {
    let view = null;

    console.log("deliveryEntity:", this.state.deliveryEntity);
    console.log("editingDelivery:", this.state.editingDelivery);

    if (this.state.deliveryEntity.length > 0) {
      view = (
        <DeliveryList
          listItems={this.state.deliveryEntity}
          onEdit={this.handleEdit}
          onDelete={this.handleDelete}
          onStatusChange={this.handleStatusChange}
        />
      );
    } else {
      view = <EmptyPostedDeliveriesView fullName={this.props.fullName} />;
    }

    return (
      <div className="container">
        {this.state.errorMessage && (
          <div className="alert alert-danger">{this.state.errorMessage}</div>
        )}
        {view}
        {this.state.editingDelivery && (
          <>
            <div className="modal-backdrop" style={modalBackdropStyle} onClick={() => this.setState({ editingDelivery: null })}></div>
            <EditDeliveryModal
              delivery={this.state.editingDelivery}
              onSave={this.handleSaveDelivery}
              onCancel={() => this.setState({ editingDelivery: null })}
            />
          </>
        )}
      </div>
    );
  }
}

// Modal backdrop style
const modalBackdropStyle: React.CSSProperties = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  zIndex: 1000,
};

// Edit modal component
interface EditDeliveryModalProps {
  delivery: DeliveryEntity;
  onSave: (updatedDelivery: DeliveryEntity) => void;
  onCancel: () => void;
}

const EditDeliveryModal: React.FC<EditDeliveryModalProps> = ({
  delivery,
  onSave,
  onCancel,
}) => {
  console.log("Rendering EditDeliveryModal with delivery:", delivery);

  const validationSchema = Yup.object({
    deliveryTitle: Yup.string().required("Title is required"),
    description: Yup.string().nullable(),
    barcode: Yup.string().nullable(),
    status: Yup.string()
      .oneOf(["IDLE", "ASSIGNED", "PICKING_UP", "DELIVERING", "DELIVERED", "DONE"], "Invalid status")
      .nullable(),
    delivery_price: Yup.number().min(0, "Price must be non-negative").nullable(),
  });

  const statusOptions = ["IDLE", "ASSIGNED", "PICKING_UP", "DELIVERING", "DELIVERED", "DONE"];

  return (
    <div className="modal" style={modalStyle}>
      <div style={{ position: "relative" }}>
        <h2>Edit Delivery</h2>
        <button
          type="button"
          className="btn btn-close"
          onClick={onCancel}
          style={{ position: "absolute", top: "10px", right: "10px" }}
        >
          ×
        </button>
        <Formik
          initialValues={delivery}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            console.log("Submitting updated delivery:", values);
            onSave({ ...values, id: delivery.id });
          }}
        >
          {({ isSubmitting }) => (
            <Form>
              <div className="form-group">
                <label>Title</label>
                <Field name="deliveryTitle" type="text" className="form-control" />
                <ErrorMessage name="deliveryTitle" component="div" className="text-danger" />
              </div>
              <div className="form-group">
                <label>Description</label>
                <Field name="description" type="text" className="form-control" />
                <ErrorMessage name="description" component="div" className="text-danger" />
              </div>
              <div className="form-group">
                <label>Barcode</label>
                <Field name="barcode" type="text" className="form-control" />
                <ErrorMessage name="barcode" component="div" className="text-danger" />
              </div>
              <div className="form-group">
                <label>Status</label>
                <Field as="select" name="status" className="form-control">
                  <option value="" disabled>
                    Select Status
                  </option>
                  {statusOptions.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </Field>
                <ErrorMessage name="status" component="div" className="text-danger" />
              </div>
              <div className="form-group">
                <label>Price</label>
                <Field name="delivery_price" type="number" className="form-control" />
                <ErrorMessage name="delivery_price" component="div" className="text-danger" />
              </div>
              <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                Save
              </button>
              <button type="button" className="btn btn-secondary" onClick={onCancel}>
                Cancel
              </button>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

// Modal style
const modalStyle: React.CSSProperties = {
  position: "fixed",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  backgroundColor: "white",
  padding: "20px",
  zIndex: 1001,
  borderRadius: "8px",
  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
  maxWidth: "500px",
  width: "90%",
  maxHeight: "80vh",
  overflowY: "auto",
};